import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

import { ToastProvider } from "@magicweave/design";

import { useViewer } from "../api/hooks";
import { claimHandOff, screenFor, useSession } from "../auth/context";
import { useCurrentOrg } from "./org";
import { rememberedStudio, rememberStudio } from "./studio-memory";
import { AppShell } from "./sidebar";
import { LoadingRows, SignedOut } from "./shell";

/**
 * The frames the routes render inside.
 *
 * Separate from router.tsx so that file exports only the router: React Fast
 * Refresh is per-module, and mixing components with other exports means every
 * edit to a layout reloads the whole app rather than swapping the component.
 *
 * The nesting mirrors what an id means. Everything under a project layout needs
 * a project; everything under an environment layout needs an environment. Each
 * layout owns the navigation for its own scope, so a screen never has to reason
 * about where it sits.
 */

export function RootLayout() {
  const { status } = useSession();

  const screen = screenFor(status);

  if (screen === "loading") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-12">
        <LoadingRows rows={3} />
      </div>
    );
  }
  // A sign-out in flight waits quietly; it must not be handed off to the
  // sign-in screen, which is where it would be signed straight back in from.
  if (screen === "waiting") return <SignedOut />;
  if (screen === "hand-off") return <HandOffToSignIn />;

  return (
    <ToastProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </ToastProvider>
  );
}

/**
 * Send a signed-out visitor to the sign-in screen rather than showing them a
 * second one.
 *
 * Control renders the screen every client shares; the console used to put a
 * bare card in front of it with a button that went to the same place. That is a
 * click and a screen for nothing, and it looked nothing like where it led.
 *
 * The redirect is cheap now that control keeps a sign-in session: somebody it
 * recognises is carried straight back, signed in, without being asked anything.
 *
 * # The one hazard
 *
 * If a session never sticks — a cookie the browser refuses, a clock far enough
 * out that every token looks expired — then redirecting on signed-out is an
 * infinite loop between two servers that each think the other should have
 * handled it. So it happens once per tab. The second time, the fallback screen
 * appears instead, with a button, and a person can decide. The same holds when
 * the sign-in lands but the session it brings back is refused at once (`stuck`).
 */
export function HandOffToSignIn() {
  const { signIn, stuck } = useSession();
  const [retrying, setRetrying] = useState(false);
  // Refs survive StrictMode's deliberate second run of an effect; the storage
  // claim would not, and deciding twice would show the fallback copy for a
  // frame on the way out.
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current) return;
    decided.current = true;
    if (!stuck && claimHandOff(sessionStorage)) {
      void signIn();
      return;
    }
    setRetrying(true);
  }, [signIn, stuck]);

  return <SignedOut retrying={retrying} stuck={stuck} />;
}

export function IndexScreen() {
  const viewer = useViewer();
  const navigate = useNavigate();
  // Nothing on screen names an organisation here, so `useCurrentOrg` falls all
  // the way back to the viewer's first membership — see its own comment. A
  // remembered studio wins over that guess, but only while it is still true:
  // a membership can be revoked between visits, and landing somebody on a
  // studio they were removed from is a 403 wearing a screen rather than the
  // guess this exists to avoid.
  const resolved = useCurrentOrg();
  const remembered = rememberedStudio();
  const stillMember = viewer.data?.orgs.some((o) => o.id === remembered) ?? false;
  const orgId = stillMember ? remembered : resolved;

  useEffect(() => {
    if (orgId) {
      void navigate({ to: "/orgs/$orgId", params: { orgId }, replace: true });
    } else if (viewer.data && viewer.data.orgs.length === 0) {
      // Genuine onboarding: nobody belongs to a studio yet. This is the one
      // case decisions/0038 leaves alone — an empty shelf is a studio with
      // nothing in it yet, not a person with nowhere to be at all.
      void navigate({ to: "/start", replace: true });
    }
  }, [orgId, navigate, viewer.data]);

  return <LoadingRows rows={3} />;
}

const SetupGuide = lazy(() =>
  import("./setup-guide/SetupGuide").then((m) => ({ default: m.SetupGuide })),
);

/**
 * Project and environment frames.
 *
 * They carry no navigation any more — it is all in the sidebar, which is the
 * point: two tab bars at two levels meant the environment destinations were
 * invisible until you were already inside an environment. What is left is the
 * nesting itself, which is still what says a screen needs an id.
 *
 * The project frame also draws the setup guide above whichever page is open.
 * It is lazy, so a game that has finished its first loop pays one small
 * request for it and the console's first load pays nothing.
 */
export function ProjectLayout() {
  const { projectId } = useParams({ from: "/projects/$projectId" });
  return (
    <>
      <Suspense fallback={null}>
        <SetupGuide projectId={projectId} />
      </Suspense>
      <Outlet />
    </>
  );
}

export function EnvironmentLayout() {
  return <Outlet />;
}

/**
 * The studio frame — decisions/0038's second scope, beside the project one
 * above.
 *
 * It must fetch nothing. The rail it sits above (`sidebar.tsx`'s `Frame`)
 * needs only `orgId`, which is already in the URL, so there is nothing here
 * for this layout to ask the API for on the studio's behalf — a studio screen
 * fetches its own data the way `TeamScreen` and `PlanScreen` already do. That
 * restraint is load-bearing rather than tidiness: a test can mock a second
 * organisation down to exactly the endpoints one screen needs (`entitlement`,
 * `members`, `invitations`), and a fixture's catch-all fails anything else —
 * so a layout that fetched, say, the project list on every studio screen would
 * take down a test it has no business touching. See
 * `e2e/journeys/credit-chip.spec.ts`'s org-switch test.
 *
 * It does write one thing: every visit to a studio screen records that studio
 * as the one `/` lands on next (`studio-memory.ts`). That is a `localStorage`
 * write, not a request, so the restraint above still holds.
 */
export function OrgLayout() {
  const { orgId } = useParams({ from: "/orgs/$orgId" });
  useEffect(() => rememberStudio(orgId), [orgId]);
  return <Outlet />;
}
